/**
 * A document a payment recorded on another one can also be linked to, as the API describes it:
 * the ones the document was created from ("before") and the ones created from it ("after").
 */
export type PaymentLinkCandidate = {
  id: string
  reference: string | null
  doc_type: string
  status: string
  emission_date: string | null
  created_at: string
  currency: string | null
  total_tvac: number | string
  total_paid: number | string
  /** Already in the accounting. */
  exported: boolean
  /** Its payments come from the accounting: a manual payment interrupts that. */
  payments_sync_enabled: boolean
}

export type PaymentLinkCandidates = {
  before: PaymentLinkCandidate[]
  after: PaymentLinkCandidate[]
  /** The ones to tick by default: nothing but an unambiguous invoice after the document. */
  preselected_ids: string[]
}

export function noPaymentLinkCandidates(): PaymentLinkCandidates {
  return { before: [], after: [], preselected_ids: [] }
}

export function hasPaymentLinkCandidates(
  candidates: PaymentLinkCandidates | null | undefined
): boolean {
  return (
    !!candidates &&
    (candidates.before.length > 0 || candidates.after.length > 0)
  )
}

/**
 * The documents ticked among the candidates whose payments are synced from the accounting:
 * linking a manual payment to one of them interrupts that sync.
 */
export function syncedCandidatesAmong(
  candidates: PaymentLinkCandidates | null | undefined,
  selectedIds: string[]
): PaymentLinkCandidate[] {
  if (!candidates) return []

  return [...candidates.before, ...candidates.after].filter(
    (candidate) =>
      candidate.payments_sync_enabled && selectedIds.includes(candidate.id)
  )
}

/**
 * "Facture 26MA100": the type then the reference of a candidate, as the user knows it.
 */
export function paymentLinkCandidateLabel(
  translate: (key: string) => string,
  candidate: PaymentLinkCandidate
): string {
  return `${translate(`enums.invoicing.docTypes.${candidate.doc_type}`)} ${candidate.reference ?? ''}`.trim()
}
